// AddTask — modal-embedded form for creating a Task row.
// Refactored 2026-05-05 (Phase 3.5, Class P) — header onto ModalFormHeader.
// PDF→PNG thumbnail pipeline (pdfjs-dist 2.16.105) + FormData (diagram + image) preserved.

import { useMemo, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import CTextField from "../CTextField/CTextField";
import {
  Button, Typography, CircularProgress, Box, Stack, Grid,
} from "@mui/material";
import { AssignmentTurnedInOutlined, PictureAsPdfOutlined } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import CSelect from "../CSelect/CSelect";
import { createTask } from "../../feature/tasksSlice";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ModalFormHeader, FieldLabel } from "../PaperForm";
import { toId, jobOptionsOf, userOptionsOf } from "../../utils/taskSelectOptions";

// PDF.js worker — CDN pin matches installed version (2.16.105)
pdfjsLib.GlobalWorkerOptions.workerSrc =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js";

// Generate a PNG Blob from PDF page 1 at 150 DPI equivalent scale
async function pdfPageOneThumbnail(pdfFile) {
  const arrayBuffer = await pdfFile.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale: 1.5 });

  const canvas = document.createElement("canvas");
  canvas.width = viewport.width;
  canvas.height = viewport.height;

  const ctx = canvas.getContext("2d");
  await page.render({ canvasContext: ctx, viewport }).promise;

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/png", 0.92);
  });
}

const AddTask = (props) => {
  const dispatch = useDispatch();

  // W-25: options as { value: id, label } (see utils/taskSelectOptions).
  const jobOptions = useMemo(() => jobOptionsOf(props.jobs), [props.jobs]);
  const userOptions = useMemo(() => userOptionsOf(props.users), [props.users]);

  const [data, setData] = useState({
    heatNo: "",
    description: "",
    projectManager: "",
    QCI: "",
    fitter: "",
    welder: "",
    painter: "",
    foreman: "",
  });

  const [userId, setSelectedUserId]     = useState(null);
  const [jobId, setJobId]               = useState(null);
  const [estimatedHour, setEstimatedHour] = useState(null);

  // diagram = original PDF file  |  thumbnailBlob = PNG blob for `image` field
  const [diagramFile, setDiagramFile]       = useState(null);
  const [thumbnailBlob, setThumbnailBlob]   = useState(null);
  const [previewDataUrl, setPreviewDataUrl] = useState("");
  const [thumbLoading, setThumbLoading]     = useState(false);

  const handleChange = (event) => {
    if (event.target.name === "estimatedHour") {
      setEstimatedHour(event.target.value);
    } else {
      setData((prev) => ({ ...prev, [event.target.name]: event.target.value }));
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

    if (isPdf) {
      setThumbLoading(true);
      setDiagramFile(file);
      setThumbnailBlob(null);
      setPreviewDataUrl("");
      try {
        const blob = await pdfPageOneThumbnail(file);
        setThumbnailBlob(blob);
        const reader = new FileReader();
        reader.onload = (e) => setPreviewDataUrl(e.target.result);
        reader.readAsDataURL(blob);
      } catch (err) {
        console.error("PDF thumbnail error:", err);
        toast.error("Could not generate thumbnail from PDF.");
        setDiagramFile(null);
      } finally {
        setThumbLoading(false);
      }
    } else {
      // Regular image
      setDiagramFile(null);
      setThumbnailBlob(null);
      const reader = new FileReader();
      reader.onload = (e) => setPreviewDataUrl(e.target.result);
      reader.readAsDataURL(file);
      setThumbnailBlob(file);
    }
  };

  const handleSubmit = async () => {
    if (!estimatedHour) {
      toast("Please Enter Estimated Hour");
      return;
    }
    const jobIdInt = toId(jobId);
    if (jobIdInt === null) {
      toast("Please Select Job");
      return;
    }
    const userIdInt = userId === null || userId === "" ? null : toId(userId);
    if (userId !== null && userId !== "" && userIdInt === null) {
      toast("Please select the assignee again");
      return;
    }
    if (!thumbnailBlob && !diagramFile) {
      toast("Please attach a Design Drawing (PDF or image)");
      return;
    }

    const formData = new FormData();
    formData.append("heatNo",           data.heatNo);
    formData.append("description",      data.description);
    formData.append("estimatedHour",    Number(estimatedHour));
    formData.append("jobId",            String(jobIdInt));
    formData.append("status",           "pending");
    // Assignee is optional on the server; it is sent only when chosen (never as "null"/"undefined").
    if (userIdInt !== null) formData.append("userId", String(userIdInt));
    formData.append("projectManager",   data.projectManager);
    formData.append("QCI",              data.QCI);
    formData.append("fitter",           data.fitter);
    formData.append("welder",           data.welder);
    formData.append("painter",          data.painter);
    formData.append("foreman",          data.foreman);

    // Thumbnail PNG → `image` field
    if (thumbnailBlob) {
      const thumbName = diagramFile
        ? diagramFile.name.replace(/\.pdf$/i, "_thumb.png")
        : "image_thumb.png";
      formData.append("image", thumbnailBlob, thumbName);
    }

    // Original PDF → `diagram` field
    if (diagramFile) {
      formData.append("diagram", diagramFile, diagramFile.name);
    }

    try {
      const res = await dispatch(createTask(formData));
      // The tasks slice shows the result: "Task created successfully." or the server's refusal.
      if (res.type === "createTask/tasks/fulfilled") {
        props.setOpen(false);
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error.message);
      toast.error(error.message || "Something went wrong. Please try again later.", {
        position: toast.POSITION.TOP_RIGHT,
      });
    }
  };

  const staffFields = [
    { name: "projectManager", label: "Project Manager" },
    { name: "QCI",            label: "QCI" },
    { name: "fitter",         label: "Fitter" },
    { name: "welder",         label: "Welder" },
    { name: "painter",        label: "Painter" },
    { name: "foreman",        label: "Foreman" },
  ];

  return (
    <Box sx={{ overflowY: "auto", maxHeight: 550, overflowX: "hidden", pr: 0.5 }}>
      <ModalFormHeader
        overline="SFM · Tasks"
        title="Add Task"
        icon={AssignmentTurnedInOutlined}
      />

      <Stack spacing={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange}
              name="heatNo" label="Heat Number"
              defaultValue={data.heatNo}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel required>Job</FieldLabel>
            <CSelect
              label=""
              data={jobOptions}
              initialValue={jobId}
              setinitialValue={setJobId}
              margin="0px"
            />
          </Grid>

          <Grid item xs={12}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange}
              name="description" label="Description"
              defaultValue={data.description}
            />
          </Grid>

          {staffFields.map(({ name, label }) => (
            <Grid item xs={12} sm={6} key={name}>
              <CTextField
                margin="0px" fullWidth onChange={handleChange}
                name={name} label={label}
                defaultValue={data[name]}
              />
            </Grid>
          ))}

          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange} type="number"
              name="estimatedHour" label="Estimated Hour"
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel required>Assign To</FieldLabel>
            <CSelect
              disabled={false}
              label=""
              data={userOptions}
              initialValue={userId}
              setinitialValue={setSelectedUserId}
              margin="0px"
              filter="users"
            />
          </Grid>
        </Grid>

        {/* Design Drawing — file upload + preview */}
        <Box
          sx={{
            border: "1px solid", borderColor: "divider",
            borderRadius: 2, p: 2, bgcolor: "#fafafa",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <PictureAsPdfOutlined sx={{ fontSize: 18, color: "text.secondary" }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", letterSpacing: "0.04em" }}>
              DESIGN DRAWING (PDF or image)
            </Typography>
          </Stack>

          {thumbLoading && (
            <Box sx={{ width: "100%", textAlign: "center", py: 2 }}>
              <CircularProgress size={28} />
              <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                Generating thumbnail from PDF…
              </Typography>
            </Box>
          )}

          {previewDataUrl && !thumbLoading && (
            <Box sx={{ width: "100%", textAlign: "center", mb: 1 }}>
              <img
                alt="design drawing preview"
                style={{ maxWidth: "100%", maxHeight: 280, objectFit: "contain", borderRadius: 4 }}
                src={previewDataUrl}
              />
              {diagramFile && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5, color: "text.secondary" }}>
                  PDF: {diagramFile.name}
                </Typography>
              )}
            </Box>
          )}

          <CTextField
            name="File"
            type="file"
            inputProps={{ accept: ".pdf,.png,.jpg,.jpeg,.gif,.webp" }}
            onChange={handleFileChange}
            margin="0px"
            fullWidth
          />
        </Box>

        <Button
          disabled={props.isLoading || thumbLoading}
          onClick={handleSubmit}
          variant="contained"
          sx={{
            width: "100%",
            mt: 1,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            letterSpacing: "0.01em",
          }}
        >
          {props.isLoading ? "Loading…" : "Create Task"}
        </Button>
      </Stack>
    </Box>
  );
};

export default AddTask;
